<template>
    <div class="assets-allocation">
        <h2>{{$t('AssetsAllocationTitle')}}</h2>
        <i18next v-if="!showIncomeFundAssetsDescription" class="assets-allocation__description" path="AssetsAllocationDescription" tag="p">
            <span>{{descriptionValues.retirementYear}}</span>
        </i18next>
        <p
            v-if="showIncomeFundAssetsDescription"
            class="assets-allocation__description"
        >
            {{$t('IncomeFundAssetsAllocationDescription')}}
        </p>
        <div class="assets-allocation__container">
            <div class="assets-allocation__chart-container">
                <div class="top-labels">
                    <div v-for="label in xDomainTop" :key="label">{{label}}</div>
                </div>
                <VerticalBar
                    ref="verticalBar"
                    :stacked="true"
                    :breakpoint="1"
                    :colors="colors"
                    :legend="false"
                    y1TickSymbol=" "
                    :x-domain="xDomain"
                    :y1-axis-data="y1AxisData"
                    :yDomain="[0, 100]"
                    :y1TickFormat="tickFunction"
                    :xLabels="xLabels"
                    y1AxisLabel="Asset Allocation %"
                    @onRender="onChartRender"
                    class="assets-allocation__chart"
                />
                <div class="assets-allocation__description chart desktop">{{$t('AssetsAllocationChartDescription')}}</div>
                <p class="assets-allocation__chart-description desktop" >{{$t('ChartDescription')}}</p>
            </div>
            <Legends :groups="groups" />
        </div>
    </div>
</template>

<script>
import _ from "lodash";
import d3 from "d3";
import VerticalBar from "./VerticalBar";
import Legends from "./Legends";
import Data from "../assets/data.json";
import MstarData from "../assets/mstarData.json";
import Logic from "../logic";
export default {
    props: {
        year: {
            type: Number,
            required: true
        },
        desiredRetirementYears: {
            type: Number,
            required: true
        }
    },
    computed: {
        showIncomeFundAssetsDescription () {
            return !Number(this.descriptionValues.retirementYear);
        },
        xLabels () {
            return _.map(this.xDomain, (x, i) => {
                if (x === "0") {
                    return "Retirement";
                }
                if (!this.isMobile && i === 0) {
                    return "Years Until Retirement";
                }
                if (!this.isMobile && i === this.xDomain.length - 2) {
                    return "Years After Retirement";
                }

                return null;
            });
        },
        selectedChartBlock () {
            return this.$el.querySelectorAll("g.mbc-vertical-bar-chart .bar-group");
        },
        groups () {
            const { index } = this.tdfRetirementYear;
            return Logic.getGroups(index, this.dataToUse);
        },
        yearFromRetirement () {
            const { desiredRetirementYears, year } = this;
            return Logic.getYearFromRetirement(desiredRetirementYears, year);
        },
        tdfRetirementYear () {
            const { yearFromRetirement } = this;
            return Logic.getTdfRetirementYear(yearFromRetirement, this.dataToUse);
        },
        descriptionValues () {
            const { labelValue: retirementYear } = this.tdfRetirementYear;
            return { retirementYear };
        },
        assetAllocation () {
            const { value } = this.tdfRetirementYear;
            return Logic.getAssetsAllocation(value, this.dataToUse);
        },
        xDomainTop () {
            return Logic.getTopLabels(this.dataToUse);
        },
        xDomain () {
            const xDomainMapper = this.isMobile ?
                (x, i, array) => {
                    if (i === 0 || i === array.length - 1 || x.label === "0") {
                        return x.label;
                    }

                    return "";
                } :
                x => x.label ? x.label : `${x.year}`;

            return _
                .chain(this.assetAllocation)
                .first()
                .get("computedValues")
                .map(xDomainMapper)
                .value();
        },
        y1AxisData () {
            return _.map(this.assetAllocation, x => {
                return {
                    values: _.map(x.computedValues, cv => cv.value),
                    name: x.name
                };
            });
        }
    },
    watch: {
        tdfRetirementYear (value) {
            const index = value.index;
            this.$emit("onTdfRetirementYearChange", index);
        },
        $route(to, from) {
            this.routeToUse = to.path.toLowerCase().includes('morningstarlifetimeindexfunds') ? 'mstar' : '';
            this.dataToUse = this.routeToUse === 'mstar' ? MstarData : Data;
            this.verticalData = this.dataToUse.assetAllocation;
            this.colors = _.map(this.dataToUse.assetAllocation.y1AxisData, y => y.color);
        }
    },
    methods: {
        onChartRender () {
            const index = this.tdfRetirementYear.index;
            this.$el.querySelectorAll("g.mbc-vertical-bar-chart .bar-group")[index].style.opacity = 1.0;
        },
        tickFunction (d) {
            const formatted = d3.format(".1")(d);
            return formatted;
        }
    },
    components: {
        VerticalBar,
        Legends
    },
    mounted () {
        const index = this.tdfRetirementYear.index;
        this.$emit("onTdfRetirementYearChange", index);

        this.isMobile = this.$refs.verticalBar && this.$refs.verticalBar.$el.clientWidth < 600;
        window.addEventListener("resize", () => {
            this.isMobile = this.$refs.verticalBar && this.$refs.verticalBar.$el.clientWidth < 600;
        });
    },
    data () {
        const routeToUse = this.$router.currentRoute.path.toLowerCase().includes('morningstarlifetimeindexfunds') ? 'mstar' : '';
        const dataToUse = routeToUse === 'mstar' ? MstarData : Data;
        return {
            routeToUse,
            dataToUse,
            verticalData: dataToUse.assetAllocation,
            colors: _.map(dataToUse.assetAllocation.y1AxisData, y => y.color),
            isMobile: false
        };
    }
};
</script>

<style lang="scss">
.assets-allocation {
    text-align: left;
    position: relative;

    &__description {
        @include mds-body-text-l();
        color: $mds-text-color-primary-default;
    }

    &__chart {
        height: 350px;
        width: 100%;
        display: inline-block;
        margin-bottom: $mds-space-2-x;

        &-container {
            flex: 15;

            p {
                @include mds-body-text-s();
                color: $mds-text-color-primary-default;
            }
        }

        .bar-group {
            opacity: $mds-opacity-overlay;
        }

        &-description {
            color: $mds-color-neutral-50;

            &.desktop {
                color: $mds-color-neutral-50;
            }
        }
    }

    .desktop {
        display: none;
    }

    .legends {
        display: inline-block;
        flex: 5;
        margin-top: $mds-space-4-x;
    }

    .top-labels {
        display: none;
    }

    @media #{$bp-desktop} {
        &__container {
            display: flex;
        }

        &__description {
            width: 80%;

            &.chart {
                width: 100%;
            }
        }

        .top-labels {
            display: flex;
            justify-content: space-between;
            margin-left: 57px;
            margin-right: 15px;
            font-size: $mds-font-size-s;
            align-items: flex-end;

            div {
                text-align: center;
                width: 6.3%;
                color: $mds-text-color-secondary-default;
            }
        }

        .legends {
            margin-top: 0;
        }

        .desktop {
            display: block;
        }

        .mobile {
            display: none;
        }
    }
}
</style>



// WEBPACK FOOTER //
// src/components/AssetsAllocation.vue